import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { isTutorialActive } from "@/tutorialFlag";
import { readDemoSid } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();

// ─── Helpers ───
function decodeJWT(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload;
  } catch (e) {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = decodeJWT(token);
  if (!payload?.exp) return false; // no expiry claim → assume valid
  return Date.now() >= payload.exp * 1000;
}

function clearSessionStorage() {
  localStorage.removeItem("mealpill_token");
  localStorage.removeItem("mealpill_onboarding_completed");
}

// ─── Provider ───
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem("mealpill_token");
    if (!t) return null;
    if (isTokenExpired(t)) {
      clearSessionStorage();
      return null;
    }
    return t;
  });
  const [loading, setLoading] = useState(!!localStorage.getItem("mealpill_token"));
  const tokenRef = useRef(token);
  const loadedRef = useRef(false);

  // Інтерсептор створюється один раз (useMemo([])) і замкнув би найперший navigate,
  // а його ідентичність у react-router змінюється. Тому тримаємо в ref і оновлюємо
  // ефектом — тим самим патерном, що вже застосований до tokenRef нижче.
  // AuthProvider стоїть усередині BrowserRouter (App.js), тож useNavigate тут легальний.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const apiInstance = useMemo(() => {
    const instance = axios.create({ baseURL: API, timeout: 60000 });
    instance.interceptors.request.use((config) => {
      const currentToken = tokenRef.current || localStorage.getItem("mealpill_token");
      if (currentToken) {
        config.headers.Authorization = `Bearer ${currentToken}`;
      }
      if (isTutorialActive()) { config.headers['X-Tutorial'] = '1'; }
      return config;
    });
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Only clear session on 401 from NON-auth endpoints.
        // /auth/me 401 на mount не повинен скидати сесію — це може бути transient.
        const url = error.config?.url || "";
        const isAuthEndpoint = url.includes('/auth/me') || url.includes('/auth/login') || url.includes('/auth/register');
        if (error.response?.status === 401 && !isAuthEndpoint) {
          clearSessionStorage();
          tokenRef.current = null;
          setToken(null);
          setUser(null);
        }

        // 402 "потрібна підписка" — єдина реакція на весь застосунок.
        // Умова СТРОГА: тільки dict-detail із reason === "subscription_required".
        // 402 з "budget_exhausted" — це ПЛАТНИК, що вичерпав ліміт днів. Він сюди
        // не потрапляє: підписки в стані не втрачає, на /subscription не їде,
        // і його обробка в MealPlannerPage лишається як була.
        const detail = error.response?.data?.detail;
        if (
          error.response?.status === 402 &&
          typeof detail === "object" &&
          detail !== null &&
          detail.reason === "subscription_required"
        ) {
          // 1) ЗАВЖДИ: лікуємо застарілу копію підписки. Корони й LockOverlay
          //    з'являються самі, бо isSubActive почне давати false.
          //    Викидаємо не user, а лише підписку; решта полів (trial_end,
          //    planning_budget_days, stripe_customer_id) лишається — SubscriptionPage
          //    рахує по них "тріал уже використано".
          setUser((prev) =>
            prev
              ? {
                  ...prev,
                  subscription: {
                    ...(prev.subscription || {}),
                    status: "inactive",
                    access_override: false,
                  },
                }
              : prev
          );
          // 2) ДОДАТКОВО: перехід лише якщо запит ініціював клік користувача.
          //    Дефолт — мовчазно: фонові тіпси/інсайти лишаються без переходу
          //    і просто отримують блюр від LockOverlay після п.1.
          //    Кінцевий слеш обрізаємо, інакше "/subscription/" пройшов би гард.
          const currentPath = window.location.pathname.replace(/\/+$/, "");
          if (error.config?.redirectOnPaywall === true && currentPath !== "/subscription") {
            navigateRef.current?.("/subscription");
          }
        }

        return Promise.reject(error);
      }
    );
    return instance;
  }, []);

  const api = useCallback(() => apiInstance, [apiInstance]);

  // ─── Initial mount: optimistic hydration from JWT ───
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    const savedToken = localStorage.getItem("mealpill_token");
    if (!savedToken) {
      setLoading(false);
      return;
    }

    const payload = decodeJWT(savedToken);
    if (!payload || isTokenExpired(savedToken)) {
      // Token invalid/expired → force re-login
      clearSessionStorage();
      setToken(null);
      tokenRef.current = null;
      setLoading(false);
      return;
    }

    // Optimistic: set minimal user from JWT payload.
    // App can render immediately, user doesn't see "logged out" flash.
    const cachedOnboarding = localStorage.getItem("mealpill_onboarding_completed") === "true";
    setUser({
      id: payload.user_id,
      email: payload.email,
      onboarding_completed: cachedOnboarding,
    });
    setLoading(false);

    // Background: fetch fresh user data, update silently.
    apiInstance
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        if (res.data?.onboarding_completed) {
          localStorage.setItem("mealpill_onboarding_completed", "true");
        }
      })
      .catch((err) => {
        // 401 here means token was invalid server-side → log out.
        // Network/5xx errors → keep optimistic user state.
        if (err.response?.status === 401) {
          clearSessionStorage();
          setToken(null);
          tokenRef.current = null;
          setUser(null);
        } else {
          console.warn("Background /auth/me failed, keeping optimistic state:", err.message);
        }
      });
  }, [apiInstance]);

  // ─── Cross-tab sync: logout in one tab → logout in all ───
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "mealpill_token" && !e.newValue) {
        // Token was removed in another tab
        setToken(null);
        tokenRef.current = null;
        setUser(null);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // ─── Login ───
  const login = async (email, password) => {
    // CRITICAL: clear any existing session first to prevent cross-user contamination.
    clearSessionStorage();
    tokenRef.current = null;

    const res = await apiInstance.post("/auth/login", { email, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem("mealpill_token", newToken);
    tokenRef.current = newToken;
    setToken(newToken);
    setUser(userData);
    // гідратуємо повний профіль (subscription тощо) — login/register віддають урізаний user
    try {
      const meRes = await apiInstance.get("/auth/me");
      setUser(meRes.data);
    } catch (e) { /* лишаємо userData якщо /auth/me недоступний */ }
    if (userData?.onboarding_completed) {
      localStorage.setItem("mealpill_onboarding_completed", "true");
    }
    return userData;
  };

  // ─── Register ───
  const register = async (email, password, name, language, termsAccepted) => {
    clearSessionStorage();
    tokenRef.current = null;

    // Third step of the demo funnel: if this browser generated a day in the
    // public demo, the signup carries that anonymous id so the two can be
    // joined. Absent for anyone who never opened the demo, and never invented.
    const demoSid = readDemoSid();
    const res = await apiInstance.post("/auth/register", {
      email,
      password,
      name,
      language,
      terms_accepted: termsAccepted === true,
      ...(demoSid ? { demo_sid: demoSid } : {}),
    });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem("mealpill_token", newToken);
    tokenRef.current = newToken;
    setToken(newToken);
    setUser(userData);
    // гідратуємо повний профіль (subscription тощо) — login/register віддають урізаний user
    try {
      const meRes = await apiInstance.get("/auth/me");
      setUser(meRes.data);
    } catch (e) { /* лишаємо userData якщо /auth/me недоступний */ }
    return userData;
  };

  // ─── Logout ───
  const logout = useCallback(() => {
    clearSessionStorage();
    tokenRef.current = null;
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = (data) => {
    setUser((prev) => (prev ? { ...prev, ...data } : data));
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateUser, api }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
